<template>
    <Row
        :class="{'UserManagementUsersTableRow--disabled': submitting}"
        class="UserManagementUsersTableRow"
        hoverable
    >
        <Cell>
            <div class="UserManagementUsersTableRow__nameAndAvatarContainer">
                <Avatar
                    class="UserManagementUsersTableRow__avatar"
                    :name="user.name"
                    :email="user.email"
                    :url="user.avatarUrl"
                />
                <UserNameBox :name="user.name" :email="user.email" :label="userLabel" />
            </div>
        </Cell>
        <Cell>
            <div v-if="roleManagedBySso" class="UserManagementUsersTableRow__governedRole">
                <span data-test="governed-role">{{ currentRoleOption.label }}</span>
                <span class="UserManagementUsersTableRow__governedCaption" data-test="governed-role-caption">
                    Set by your identity provider group mapping.
                    <a href="#/configuration/sso" data-test="governed-role-link">Manage on the Single sign-on page</a>
                </span>
            </div>
            <NewDropdown
                v-else
                :options="roleOptions"
                :selected="currentRoleOption"
                :disabled="submitting"
                @input="onRoleChange"
            />
        </Cell>
        <Cell class="UserManagementUsersTableRow__actionsCell">
            <div class="UserManagementUsersTableRow__actionsContainer">
                <NewButton
                    v-if="user.status !== 'active'"
                    v-tooltip="'Resend invite'"
                    variant="flat"
                    size="icon-sm"
                    color="secondary"
                    :disabled="submitting"
                    @click="emitAction('resendInvite')"
                >
                    <EidIcon :icon-src="faEnvelope" />
                </NewButton>
                <NewButton
                    v-tooltip="'Edit'"
                    variant="flat"
                    size="icon-sm"
                    color="secondary"
                    :disabled="submitting"
                    @click="emitAction('edit')"
                >
                    <EidIcon :icon-src="faPenToSquare" />
                </NewButton>
                <NewButton
                    v-tooltip="'Delete'"
                    variant="flat"
                    size="icon-sm"
                    color="secondary"
                    :disabled="submitting"
                    @click="emitAction('delete')"
                >
                    <EidIcon :icon-src="faXmark" />
                </NewButton>
            </div>
        </Cell>
    </Row>
</template>

<style lang="scss">
// stylelint-disable selector-max-combinators, selector-max-compound-selectors
.UserManagementUsersTableRow {
    &__nameAndAvatarContainer {
        display: flex;
        align-items: center;
    }

    & &__avatar {
        font-size: 12px;
        margin-right: 10px;
        padding-right: 0;
        padding-top: 0;
        padding-bottom: 0;
    }

    &__governedRole {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        color: var(--eidDarkBlue);
        font-size: var(--text-body-size);
    }

    &__governedCaption {
        color: var(--eidTechGray);
        font-size: var(--text-caption-size);
    }

    &__governedCaption a {
        color: var(--eidSecureBlue);
    }

    & &__actionsCell .Expandable__button {
        font-size: 1.8em;
    }

    & &__actionsContainer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 0.25rem;
    }

    &--disabled &__actionsContainer {
        opacity: 0.3;
        cursor: not-allowed;

        > * {
            pointer-events: none;
        }
    }

    .NewDropdown .q-field__control {
        background-color: #fff;
        height: 45px;
        padding: 0 15px;

        &:hover::before {
            border: 1px solid #aaa;
        }
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { faXmark, faPenToSquare, faEnvelope } from '@fortawesome/pro-light-svg-icons';

    import { Avatar } from '@evidentid/dashboard-commons/components/Avatar';
    import { DropdownOption, NewDropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import { RbacRoleDetails } from '@evidentid/tprm-portal-lib/models/rbac';
    import type { User } from '@evidentid/tprm-portal-lib/models/user-management/User.model';

    import NewButton from '@/components/new-button/NewButton.vue';

    import UserActionsMenu from './UserActionsMenu.vue';
    import UserNameBox from './UserNameBox.vue';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    @Component({
        emits: [ 'change-role', 'delete', 'edit', 'resendInvite' ],
        components: {
            NewDropdown, Avatar, Cell, EidIcon, Row, UserActionsMenu, UserNameBox, NewButton,
        },
    })
    export default class UsersTableRow extends Vue {
        private faEnvelope = faEnvelope;
        private faPenToSquare = faPenToSquare;
        private faXmark = faXmark;

        @Prop({ type: Object })
        private user!: User;

        @Prop({ type: Boolean, default: false })
        private submitting!: boolean;

        @Prop({ type: String, default: null })
        private userLabel!: string | null;

        // Whether the relying party has SSO dynamic creation on. Only then is the role of a user
        // who signs in through the federated connection re-resolved from IdP groups at each login.
        @Prop({ type: Boolean, default: false })
        private dynamicCreationEnabled!: boolean;

        // The connection that federates this relying party's logins. Null while the SSO view loads.
        @Prop({ type: String, default: null })
        private ssoConnectionName!: string | null;

        /** Whether the Auth0 Action re-resolves this user's role at each login, which is what the
         * read-only treatment claims.
         *
         * 🚨 IT IS NOT ENOUGH THAT THE USER IS FEDERATED. The Action governs roles only on the
         * connection carrying this relying party's `tprm_rp`, so a user federated through anything
         * else -- a plain Google social login, an enterprise connection serving something else --
         * is governed by nothing. Rendering their role read-only would strand it: nothing would
         * ever re-resolve it and no administrator could edit it again. Matching on the connection
         * name is what tells the two apart.
         *
         * Every uncertain case resolves to false (editable), which is the recoverable direction.
         */
        private get roleManagedBySso(): boolean {
            return this.dynamicCreationEnabled
                && this.ssoConnectionName !== null
                && (this.user.federatedConnections || []).includes(this.ssoConnectionName);
        }

        private get roles(): RbacRoleDetails[] {
            return useRbacStore().userRoles?.list || [];
        }

        private get roleOptions(): DropdownOption[] {
            return this.roles.map(({ name, label }) => ({ label, value: name }));
        }

        private get currentRoleOption(): DropdownOption {
            return this.roleOptions.find((option) => option.value === this.user.role) ||
                { label: this.user.role, value: this.user.role };
        }

        private onRoleChange(option: DropdownOption): void {
            this.$emit('change-role', option.value);
        }

        private emitAction(action: string): void {
            if (!this.submitting) {
                this.$emit(action);
            }
        }
    }
</script>
